import {
    createId,
    nowIso,
    getState,
    mutate,
    findUnit,
    resolveBoardAndSector,
    delay,
} from './store';
import { matchesRule } from '@/utils/corporate/accessRules';
import { normalizeAssignment } from './courseAssignments';

function enrichUser(user, snapshot = getState()) {
    const board = user.boardId ? findUnit(user.boardId, snapshot) : null;
    const sector = user.sectorId ? findUnit(user.sectorId, snapshot) : null;
    const role = user.roleId ? findUnit(user.roleId, snapshot) : null;
    const manager = user.managerUserId
        ? snapshot.users.find((u) => u.id === user.managerUserId)
        : null;

    return {
        ...user,
        type: Number(user.type) === 2 || Number(user.type) === 3
            ? Number(user.type)
            : (user.corporatePermission === 'colaborador' ? 3 : 2),
        isResponsible: Boolean(user.isResponsible),
        boardName: board?.name || null,
        sectorName: sector?.name || null,
        roleName: role?.name || null,
        managerName: manager?.name || null,
        enrolmentsCount: (user.enrolments || []).length,
        completedCourses: (user.enrolments || []).filter((e) => e.status === 'completed').length,
        overdueCourses: (user.enrolments || []).filter((e) => e.status === 'overdue').length,
    };
}

function applyMandatoryCourses(user, snapshot) {
    const enrolments = [...(user.enrolments || [])];
    const role = user.roleId ? findUnit(user.roleId, snapshot) : null;
    const courseIds = new Set(role?.courseIds || []);

    snapshot.courseAssignments.forEach((raw) => {
        const assignment = normalizeAssignment(raw, snapshot);
        if (!assignment || !assignment.mandatory) return;
        if (assignment.userId && assignment.userId === user.id) {
            if (assignment.resourceType === 'course') {
                courseIds.add(assignment.resourceId);
            }
            return;
        }
        if (!matchesRule(assignment, {
            sectorId: user.sectorId,
            roleId: user.roleId,
            boardId: user.boardId,
        })) {
            return;
        }
        if (assignment.resourceType === 'course') {
            courseIds.add(assignment.resourceId);
            return;
        }
        if (assignment.resourceType === 'group') {
            const group = (snapshot.courseGroups || []).find(
                (g) => String(g.id) === String(assignment.resourceId),
            );
            (group?.items || []).forEach((item) => {
                if (item.courseId) courseIds.add(String(item.courseId));
            });
        }
    });

    // Marca obrigatoriedade: cursos fora da estrutura atual deixam de ser obrigatórios
    enrolments.forEach((enrolment) => {
        enrolment.mandatory = courseIds.has(enrolment.courseId);
    });

    courseIds.forEach((courseId) => {
        const exists = enrolments.some((e) => e.courseId === courseId);
        if (!exists) {
            const course = snapshot.courses.find((c) => c.id === courseId);
            enrolments.push({
                courseId,
                courseName: course?.name || courseId,
                status: 'not_started',
                progress: 0,
                mandatory: true,
                source: 'role',
            });
        }
    });

    user.enrolments = enrolments;
    return user;
}

export default {
    async list(filters = {}) {
        await delay();
        const snapshot = getState();
        let users = snapshot.users.map((u) => enrichUser(u, snapshot));

        if (filters.search) {
            const term = filters.search.toLowerCase();
            users = users.filter(
                (u) => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term),
            );
        }
        if (filters.boardId) users = users.filter((u) => u.boardId === filters.boardId);
        if (filters.sectorId) users = users.filter((u) => u.sectorId === filters.sectorId);
        if (filters.roleId) users = users.filter((u) => u.roleId === filters.roleId);
        if (filters.managerUserId) users = users.filter((u) => u.managerUserId === filters.managerUserId);
        if (filters.status) users = users.filter((u) => u.status === filters.status);
        if (Number(filters.type) === 2) users = users.filter((u) => Number(u.type) === 2);
        if (Number(filters.type) === 3) users = users.filter((u) => Number(u.type) === 3);
        if (filters.isResponsible === true) users = users.filter((u) => u.isResponsible);
        if (filters.isResponsible === false) users = users.filter((u) => !u.isResponsible);
        if (filters.progress) {
            if (filters.progress === 'low') users = users.filter((u) => u.progressPercent < 40);
            if (filters.progress === 'medium') users = users.filter((u) => u.progressPercent >= 40 && u.progressPercent < 80);
            if (filters.progress === 'high') users = users.filter((u) => u.progressPercent >= 80);
        }

        const page = Number(filters.page || 0);
        const size = Number(filters.size || 20);
        const start = page * size;
        const content = users.slice(start, start + size);
        const active = users.filter((u) => u.status === 'active').length;
        const responsible = users.filter((u) => u.isResponsible).length;
        const avgProgress = users.length
            ? Math.round(users.reduce((sum, u) => sum + (Number(u.progressPercent) || 0), 0) / users.length)
            : 0;

        return {
            content,
            totalElements: users.length,
            page,
            size,
            summary: {
                active,
                responsible,
                avgProgress,
            },
        };
    },

    async getById(id) {
        await delay();
        const snapshot = getState();
        const user = snapshot.users.find((u) => u.id === id);
        if (!user) {
            const error = new Error('Colaborador não encontrado.');
            error.code = 'NOT_FOUND';
            throw error;
        }
        const enriched = enrichUser(user, snapshot);
        const enrolments = enriched.enrolments || [];
        const progress = {
            userId: id,
            totalAssigned: enrolments.length,
            started: enrolments.filter((e) => e.status === 'in_progress' || e.progress > 0).length,
            completed: enrolments.filter((e) => e.status === 'completed').length,
            overdue: enrolments.filter((e) => e.status === 'overdue').length,
            averageGrade: 8.2,
            certificates: enrolments.filter((e) => e.status === 'completed').length,
            gamificationPoints: enriched.gamificationPoints || 0,
            gamificationLevel: enriched.gamificationLevel || 'Iniciante',
            enrolments: enrolments.filter((e) => e.status === 'completed'),
        };
        const timelineRes = await this.getTimeline(id);
        return {
            ...enriched,
            progress,
            timeline: timelineRes.content || [],
        };
    },

    async updateOrganization(id, payload) {
        await delay();
        return mutate((draft) => {
            const index = draft.users.findIndex((u) => u.id === id);
            if (index < 0) {
                const error = new Error('Colaborador não encontrado.');
                error.code = 'NOT_FOUND';
                throw error;
            }

            const previous = { ...draft.users[index] };
            let nextBoardId = payload.boardId !== undefined ? payload.boardId : previous.boardId;
            let nextSectorId = payload.sectorId !== undefined ? payload.sectorId : previous.sectorId;
            let nextRoleId = payload.roleId !== undefined ? payload.roleId : previous.roleId;

            if (payload.roleId) {
                const resolved = resolveBoardAndSector(payload.roleId, draft);
                nextRoleId = payload.roleId;
                nextSectorId = resolved.sector?.id || nextSectorId;
                nextBoardId = resolved.board?.id || nextBoardId;
            } else if (payload.sectorId) {
                const sector = findUnit(payload.sectorId, draft);
                nextSectorId = payload.sectorId;
                nextBoardId = sector?.parentId || nextBoardId;
            }

            draft.users[index] = {
                ...previous,
                boardId: nextBoardId,
                sectorId: nextSectorId,
                roleId: nextRoleId,
                managerUserId: payload.managerUserId !== undefined ? payload.managerUserId : previous.managerUserId,
                admissionDate: payload.admissionDate !== undefined ? payload.admissionDate : previous.admissionDate,
                internalCode: payload.internalCode !== undefined ? payload.internalCode : previous.internalCode,
                status: payload.status !== undefined ? payload.status : previous.status,
                updatedAt: nowIso(),
            };

            applyMandatoryCourses(draft.users[index], draft);

            draft.orgHistory.push({
                id: createId('hist'),
                userId: id,
                action: 'organization_updated',
                previous: {
                    boardId: previous.boardId,
                    sectorId: previous.sectorId,
                    roleId: previous.roleId,
                    managerUserId: previous.managerUserId,
                    status: previous.status,
                },
                next: {
                    boardId: draft.users[index].boardId,
                    sectorId: draft.users[index].sectorId,
                    roleId: draft.users[index].roleId,
                    managerUserId: draft.users[index].managerUserId,
                    status: draft.users[index].status,
                },
                createdAt: nowIso(),
            });

            return enrichUser(draft.users[index], draft);
        });
    },

    async getProgress(id) {
        await delay();
        const user = await this.getById(id);
        const enrolments = user.enrolments || [];
        return {
            userId: id,
            totalAssigned: enrolments.length,
            started: enrolments.filter((e) => e.status === 'in_progress' || e.progress > 0).length,
            completed: enrolments.filter((e) => e.status === 'completed').length,
            overdue: enrolments.filter((e) => e.status === 'overdue').length,
            averageGrade: 8.2,
            certificates: enrolments.filter((e) => e.status === 'completed').length,
            gamificationPoints: user.gamificationPoints || 0,
            gamificationLevel: user.gamificationLevel || 'Iniciante',
            enrolments,
        };
    },

    async getTimeline(id) {
        await delay();
        const snapshot = getState();
        const history = snapshot.orgHistory
            .filter((h) => h.userId === id)
            .map((h) => ({
                id: h.id,
                type: h.action,
                description: 'Alteração organizacional',
                createdAt: h.createdAt,
                payload: h,
            }));

        const user = snapshot.users.find((u) => u.id === id);
        const enrolmentEvents = (user?.enrolments || []).map((e, index) => ({
            id: `enrol_${id}_${index}`,
            type: e.status === 'completed' ? 'course_completed' : 'user_enrolled',
            description: e.status === 'completed'
                ? `Curso concluído: ${e.courseName}`
                : `Matriculado em: ${e.courseName}`,
            createdAt: user.updatedAt,
            payload: e,
        }));

        return {
            content: [...history, ...enrolmentEvents].sort(
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
            ),
        };
    },

    async enrollInCourse(userId, { courseId, courseName }) {
        await delay();
        return mutate((draft) => {
            const user = draft.users.find((u) => u.id === userId);
            if (!user) {
                const error = new Error('Colaborador não encontrado.');
                error.code = 'NOT_FOUND';
                throw error;
            }
            const exists = (user.enrolments || []).some((e) => e.courseId === courseId);
            if (!exists) {
                user.enrolments = user.enrolments || [];
                user.enrolments.push({
                    courseId,
                    courseName: courseName || courseId,
                    status: 'not_started',
                    progress: 0,
                    mandatory: false,
                    source: 'user',
                });
                user.updatedAt = nowIso();
            }
            return enrichUser(user, draft);
        });
    },

    async listManagers() {
        await delay();
        const snapshot = getState();
        const managers = snapshot.users.filter((u) => u.isResponsible && u.status === 'active');
        return {
            content: managers.map((u) => ({
                id: u.id,
                name: u.name,
                email: u.email,
                isResponsible: true,
            })),
        };
    },

    async create(payload) {
        await delay();
        return mutate((draft) => {
            const email = (payload.email || '').trim().toLowerCase();
            if (!payload.name?.trim() || !email) {
                const error = new Error('Nome e e-mail são obrigatórios.');
                error.code = 'VALIDATION';
                throw error;
            }
            if (!String(payload.socialNumber || '').trim()) {
                const error = new Error('CPF é obrigatório.');
                error.code = 'VALIDATION';
                throw error;
            }
            if (draft.users.some((u) => u.email.toLowerCase() === email)) {
                const error = new Error('Já existe um usuário com este e-mail.');
                error.code = 'DUPLICATE';
                throw error;
            }

            let boardId = null;
            let sectorId = payload.sectorId || null;
            let roleId = payload.roleId || null;
            if (roleId) {
                const resolved = resolveBoardAndSector(roleId, draft);
                sectorId = resolved.sector?.id || sectorId;
                boardId = resolved.board?.id || null;
            } else if (sectorId) {
                const sector = findUnit(sectorId, draft);
                boardId = sector?.type === 'board' ? sector.id : (sector?.parentId && findUnit(sector.parentId, draft)?.type === 'board'
                    ? sector.parentId
                    : null);
            }

            const user = {
                id: createId('user'),
                companyId: draft.companyId,
                name: payload.name.trim(),
                email,
                photo: payload.photo || '',
                socialNumber: payload.socialNumber || '',
                idNumber: payload.idNumber || '',
                phone: payload.phone || '',
                postcode: payload.postcode || '',
                address: payload.address || '',
                addressNumber: payload.addressNumber || '',
                addressComplement: payload.addressComplement || '',
                neighborhood: payload.neighborhood || '',
                city: payload.city || '',
                county: payload.county || '',
                country: payload.country || 'br',
                corporatePermission: payload.corporatePermission || 'colaborador',
                type: Number(payload.type) === 2 ? 2 : 3,
                boardId,
                sectorId,
                roleId,
                managerUserId: payload.managerUserId || null,
                isResponsible: Boolean(payload.isResponsible),
                admissionDate: payload.admissionDate || null,
                internalCode: payload.internalCode || '',
                status: payload.status || 'active',
                progressPercent: 0,
                enrolments: [],
                gamificationPoints: 0,
                gamificationLevel: 'Iniciante',
                createdAt: nowIso(),
                updatedAt: nowIso(),
            };

            applyMandatoryCourses(user, draft);
            draft.users.push(user);
            return enrichUser(user, draft);
        });
    },

    async update(id, payload) {
        await delay();
        return mutate((draft) => {
            const index = draft.users.findIndex((u) => u.id === id);
            if (index < 0) {
                const error = new Error('Usuário não encontrado.');
                error.code = 'NOT_FOUND';
                throw error;
            }

            const previous = draft.users[index];
            const email = payload.email !== undefined
                ? String(payload.email).trim().toLowerCase()
                : previous.email;

            if (payload.email !== undefined) {
                const duplicated = draft.users.some(
                    (u) => u.id !== id && u.email.toLowerCase() === email,
                );
                if (duplicated) {
                    const error = new Error('Já existe um usuário com este e-mail.');
                    error.code = 'DUPLICATE';
                    throw error;
                }
            }

            let boardId = previous.boardId;
            let sectorId = payload.sectorId !== undefined ? payload.sectorId : previous.sectorId;
            let roleId = payload.roleId !== undefined ? payload.roleId : previous.roleId;

            if (payload.roleId) {
                const resolved = resolveBoardAndSector(payload.roleId, draft);
                roleId = payload.roleId;
                sectorId = resolved.sector?.id || sectorId;
                boardId = resolved.board?.id || null;
            } else if (payload.sectorId) {
                sectorId = payload.sectorId;
                boardId = null;
            }

            const personalFields = [
                'photo',
                'socialNumber',
                'idNumber',
                'phone',
                'postcode',
                'address',
                'addressNumber',
                'addressComplement',
                'neighborhood',
                'city',
                'county',
                'country',
            ];
            const personalPatch = {};
            personalFields.forEach((field) => {
                if (payload[field] !== undefined) personalPatch[field] = payload[field];
            });

            draft.users[index] = {
                ...previous,
                ...personalPatch,
                name: payload.name !== undefined ? payload.name.trim() : previous.name,
                email,
                boardId,
                sectorId,
                roleId,
                managerUserId: payload.managerUserId !== undefined
                    ? payload.managerUserId
                    : previous.managerUserId,
                isResponsible: payload.isResponsible !== undefined
                    ? Boolean(payload.isResponsible)
                    : Boolean(previous.isResponsible),
                admissionDate: payload.admissionDate !== undefined
                    ? payload.admissionDate
                    : previous.admissionDate,
                internalCode: payload.internalCode !== undefined
                    ? payload.internalCode
                    : previous.internalCode,
                status: payload.status !== undefined ? payload.status : previous.status,
                type: payload.type !== undefined
                    ? (Number(payload.type) === 2 ? 2 : 3)
                    : previous.type,
                updatedAt: nowIso(),
            };

            applyMandatoryCourses(draft.users[index], draft);
            return enrichUser(draft.users[index], draft);
        });
    },

    async remove(id) {
        await delay();
        return mutate((draft) => {
            const exists = draft.users.some((u) => u.id === id);
            if (!exists) {
                const error = new Error('Usuário não encontrado.');
                error.code = 'NOT_FOUND';
                throw error;
            }
            draft.units.forEach((unit) => {
                if (unit.managerUserId === id) unit.managerUserId = null;
            });
            draft.users.forEach((user) => {
                if (user.managerUserId === id) user.managerUserId = null;
            });
            draft.users = draft.users.filter((u) => u.id !== id);
            return { success: true };
        });
    },
};
